import { useState } from 'react';
import { Button, Modal, Space, Input, message } from 'antd';
import { EyeOutlined, LinkOutlined, SendOutlined, RollbackOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import Tiptap from './editor/Tiptap';

interface Props {
  id: string;
  status: string;
  title: string;
  bodyJson: string | null;
}

/** 預覽（後台內）／產生客戶預覽連結／發佈・退回。 */
export default function PublishActions({ id, status, title, bodyJson }: Props) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['article', id] });

  const tokenM = useMutation({
    mutationFn: () => api.previewToken(id),
    onSuccess: ({ url }) => setLink(url),
    onError: (e: Error) => message.error(e.message),
  });
  const publishM = useMutation({
    mutationFn: () => api.publishArticle(id),
    onSuccess: () => {
      message.success('已發佈');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });
  const unpublishM = useMutation({
    mutationFn: () => api.unpublishArticle(id),
    onSuccess: () => {
      message.success('已退回草稿');
      refresh();
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <Space wrap>
      <Button icon={<EyeOutlined />} onClick={() => setPreview(true)}>
        後台預覽
      </Button>
      <Button icon={<LinkOutlined />} loading={tokenM.isPending} onClick={() => tokenM.mutate()}>
        產生客戶預覽連結
      </Button>
      {status === 'published' ? (
        <Button
          danger
          icon={<RollbackOutlined />}
          loading={unpublishM.isPending}
          onClick={() => unpublishM.mutate()}
        >
          退回草稿
        </Button>
      ) : (
        <Button
          type="primary"
          icon={<SendOutlined />}
          loading={publishM.isPending}
          onClick={() => publishM.mutate()}
        >
          立即發佈
        </Button>
      )}

      <Modal
        title={`預覽：${title || '未命名'}`}
        open={preview}
        onCancel={() => setPreview(false)}
        footer={null}
        width={800}
      >
        <Tiptap initialJson={bodyJson} initialHtml={null} readOnly />
      </Modal>

      <Modal title="客戶預覽連結" open={!!link} onCancel={() => setLink(null)} footer={null}>
        <p className="mb-2 text-gray-500">複製以下連結給客戶確認（7 天內有效）：</p>
        <Input.Search
          value={link ?? ''}
          enterButton="複製"
          onSearch={() => {
            if (link) navigator.clipboard.writeText(link);
            message.success('已複製');
          }}
        />
      </Modal>
    </Space>
  );
}
